import { isCanvasFile, isHtmlFile } from '@littlebird/canvas';
import { format } from 'date-fns/format';

export { isCanvasFile, isHtmlFile };

const PASTED_TEXT_FILENAME_PATTERN = /^pasted-text-\d+T\d+\.txt$/;

export function isPastedTextFile(
  contentType: string | undefined,
  filename: string
): boolean {
  return (
    contentType === 'text/plain' && PASTED_TEXT_FILENAME_PATTERN.test(filename)
  );
}

const TEXT_PREVIEWABLE_MIMES = [
  'text/plain',
  'text/markdown',
  'application/json',
  'text/csv',
  'text/tab-separated-values',
  'text/yaml',
  'application/x-yaml',
  'text/calendar',
];

const TEXT_PREVIEWABLE_EXTENSIONS = [
  'md',
  'json',
  'csv',
  'tsv',
  'txt',
  'log',
  'ics',
  'yml',
  'yaml',
  'xml',
  'js',
  'ts',
  'tsx',
  'jsx',
  'py',
  'rb',
  'sh',
  'css',
  'html',
];

export function isTextPreviewable(
  contentType: string | undefined,
  filename: string
): boolean {
  if (
    contentType &&
    TEXT_PREVIEWABLE_MIMES.some((m) => contentType.startsWith(m))
  ) {
    return true;
  }
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? TEXT_PREVIEWABLE_EXTENSIONS.includes(ext) : false;
}

const IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'heic',
  'heif',
  'avif',
  'bmp',
  'svg',
];

type NamedFile = { filename: string };

export function selectAdaptiveImageVariants<T extends NamedFile>(
  files: T[],
  mode: 'light' | 'dark'
): T[] {
  const byName = new Map(
    files.map((file) => [file.filename.toLowerCase(), file])
  );

  return files.flatMap((file) => {
    const lightName = file.filename.replace(/\.dark(\.[^.]+)$/i, '$1');
    if (lightName !== file.filename) {
      return byName.has(lightName.toLowerCase()) ? [] : [file];
    }

    const extensionIndex = file.filename.lastIndexOf('.');
    const darkName =
      extensionIndex > 0
        ? `${file.filename.slice(0, extensionIndex)}.dark${file.filename.slice(extensionIndex)}`
        : null;
    const darkVariant = darkName ? byName.get(darkName.toLowerCase()) : null;
    return [mode === 'dark' && darkVariant ? darkVariant : file];
  });
}

export function isImageFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType?.startsWith('image/')) return true;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.includes(ext) : false;
}

export function isPdfFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === 'application/pdf') return true;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
}

export function isJsonFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === 'application/json') return true;
  return filename.split('.').pop()?.toLowerCase() === 'json';
}

export function isCalendarFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === 'text/calendar') return true;
  return filename.split('.').pop()?.toLowerCase() === 'ics';
}

export function isMarkdownFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === 'text/markdown') return true;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'md' || ext === 'markdown';
}

const WORD_DOC_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const EXCEL_WORKBOOK_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PRESENTATION_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';

export function isWordDocFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === WORD_DOC_MIME) return true;
  return filename.split('.').pop()?.toLowerCase() === 'docx';
}

export function isExcelWorkbookFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === EXCEL_WORKBOOK_MIME) return true;
  return filename.split('.').pop()?.toLowerCase() === 'xlsx';
}

export function isPresentationFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType === PRESENTATION_MIME) return true;
  return filename.split('.').pop()?.toLowerCase() === 'pptx';
}

const SPREADSHEET_MIMES = [
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.ms-excel',
  EXCEL_WORKBOOK_MIME,
  'application/vnd.oasis.opendocument.spreadsheet',
];

const SPREADSHEET_EXTENSIONS = ['csv', 'tsv', 'xlsx', 'xlsm', 'xls', 'ods'];

const ARCHIVE_MIMES = [
  'application/zip',
  'application/x-zip-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
  'application/x-7z-compressed',
  'application/vnd.rar',
  'application/x-rar-compressed',
];

const ARCHIVE_EXTENSIONS = ['zip', 'tar', 'gz', 'tgz', '7z', 'rar'];

export function isSpreadsheetFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType && SPREADSHEET_MIMES.includes(contentType)) return true;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? SPREADSHEET_EXTENSIONS.includes(ext) : false;
}

export function isArchiveFile(
  contentType: string | undefined,
  filename: string
): boolean {
  if (contentType && ARCHIVE_MIMES.includes(contentType)) return true;
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? ARCHIVE_EXTENSIONS.includes(ext) : false;
}

export function stripFileExtension(filename: string): string {
  return filename.replace(/\.[^./\\]+$/, '');
}

export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) return 'FILE';
  const ext = parts.pop()?.toUpperCase() ?? '';
  return ext || 'FILE';
}

export function getUploadFileExtension(filename: string): string | null {
  const lastDotIndex = filename.lastIndexOf('.');
  const hasExtension = lastDotIndex >= 0 && lastDotIndex < filename.length - 1;
  return hasExtension ? filename.slice(lastDotIndex + 1).toLowerCase() : null;
}

const TIMESTAMP_PREFIX_PATTERN = /^1\d{9,12}_/;

export function getDisplayFilename(filename: string): string {
  return filename.replace(TIMESTAMP_PREFIX_PATTERN, '').replaceAll('_', ' ');
}

/** `images_2026-09-22_12.02PM.zip` */
export function imagesZipFilename(now: Date): string {
  return `images_${format(now, 'yyyy-MM-dd_h.mmaa')}.zip`;
}

/** Basename of a slash-delimited path (trailing slashes stripped); the whole string if no slash. */
export function pathBasename(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed.split('/').pop() || trimmed;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Truncates filename in the middle, preserving the extension.
 * @example truncateFilename("very-long-filename.txt", 15) // "very-l...me.txt"
 */
export function truncateFilename(
  filename: string,
  maxLength: number = 20
): string {
  if (filename.length <= maxLength) return filename;

  const lastDot = filename.lastIndexOf('.');
  const ext = lastDot > 0 ? filename.slice(lastDot) : '';
  const name = lastDot > 0 ? filename.slice(0, lastDot) : filename;

  const availableLength = maxLength - ext.length - 3;
  if (availableLength <= 0) return filename.slice(0, maxLength - 3) + '...';

  const startLength = Math.ceil(availableLength * 0.6);
  const endLength = availableLength - startLength;

  return name.slice(0, startLength) + '...' + name.slice(-endLength) + ext;
}

const mimeNameMap: Record<string, string> = {
  'application/vnd.littlebird.canvas+json': 'Canvas',
  'video/quicktime': 'QuickTime Movie',
  'video/mp4': 'MP4 Video',
  'video/x-msvideo': 'AVI Video',
  'video/x-matroska': 'Matroska Video',
  'video/webm': 'WebM Video',
  'video/mpeg': 'MPEG Video',
  'image/jpeg': 'JPEG Image',
  'image/png': 'PNG Image',
  'image/gif': 'GIF Image',
  'image/webp': 'WebP Image',
  'image/heic': 'HEIC Image',
  'image/heif': 'HEIF Image',
  'application/json': 'JSON',
  'application/pdf': 'PDF Document',
  'application/zip': 'ZIP Archive',
  'application/x-zip-compressed': 'ZIP Archive',
  'application/x-rar-compressed': 'RAR Archive',
  [WORD_DOC_MIME]: 'Word Document',
  'application/msword': 'Word Document',
  'application/vnd.ms-excel': 'Excel Spreadsheet',
  [EXCEL_WORKBOOK_MIME]: 'Excel Spreadsheet',
  'application/vnd.ms-powerpoint': 'PowerPoint Presentation',
  [PRESENTATION_MIME]: 'PowerPoint Presentation',
  'text/plain': 'Plain Text',
  'text/markdown': 'Markdown',
  'text/csv': 'CSV',
  'text/tab-separated-values': 'TSV',
  'text/calendar': 'Calendar Event',
  'text/html': 'HTML',
  'application/x-tar': 'TAR Archive',
  'application/gzip': 'Gzip Archive',
  'application/x-gzip': 'Gzip Archive',
  'application/x-7z-compressed': '7z Archive',
  'application/vnd.rar': 'RAR Archive',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
  'audio/mpeg': 'MP3 Audio',
  'audio/wav': 'WAV Audio',
  'audio/ogg': 'OGG Audio',
  'application/octet-stream': 'Binary File',
};

const extNameMap: Record<string, string> = {
  canvas: 'Canvas',
  md: 'Markdown',
  json: 'JSON',
  ics: 'Calendar Event',
  png: 'PNG Image',
  jpg: 'JPEG Image',
  jpeg: 'JPEG Image',
  gif: 'GIF Image',
  webp: 'WebP Image',
  csv: 'CSV',
  tsv: 'TSV',
  xlsx: 'Excel Spreadsheet',
  xlsm: 'Excel Spreadsheet',
  xls: 'Excel Spreadsheet',
  ods: 'OpenDocument Spreadsheet',
  docx: 'Word Document',
  pptx: 'PowerPoint Presentation',
  zip: 'ZIP Archive',
  tar: 'TAR Archive',
  gz: 'Gzip Archive',
  tgz: 'Gzip Archive',
  '7z': '7z Archive',
  rar: 'RAR Archive',
  txt: 'Plain Text',
  log: 'Log',
  yml: 'YAML',
  yaml: 'YAML',
  xml: 'XML',
  html: 'HTML',
  css: 'CSS',
  js: 'JavaScript',
  ts: 'TypeScript',
  tsx: 'TypeScript',
  jsx: 'JavaScript',
  py: 'Python',
  rb: 'Ruby',
  sh: 'Shell Script',
};

export function getFriendlyMimeName(
  mime: string | undefined,
  filename?: string
): string {
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (
      ext &&
      extNameMap[ext] &&
      (!mime ||
        mime === 'file' ||
        mime === 'text/plain' ||
        mime === 'application/octet-stream')
    ) {
      return extNameMap[ext];
    }
  }

  if (!mime || mime === 'file') return 'File';
  return mimeNameMap[mime] || mime;
}
